/**
 * `fal.subscribe` for every caller of `getFalClient()`, built on the once-only queue boundary.
 *
 * The SDK's own subscribe retries the paid submit on 429/502/503/504, and the proxy transport
 * can fail a submit over to a second target, so one call can enqueue and bill several jobs.
 * Legacy services keep their `fal.subscribe(endpoint, options)` call shape; underneath it is
 * exactly one POST followed by bounded reads of that one job.
 */
import { subscribeFalQueueOnceWithCancellation } from './cancelableFalSubscription.js';
import { isCancellationError } from './utils.js';
import { captureSubsystemError } from '../sentry';

// The SDK subscribe had no deadline, and legacy callers include long video and 3D jobs. This is a
// read deadline only: the provider job keeps running and its request ID stays recoverable
// (AGENT_COLLABORATION_CONTRACT.md, "an active read deadline does not cancel the provider job").
const LEGACY_SUBSCRIBE_TIMEOUT_MS = 30 * 60_000;
// The SDK polled status every 500 ms; keeping that pace keeps cheap LLM calls as fast as before.
const LEGACY_POLL_INTERVAL_MS = 500;
// At that pace, tolerate about ten seconds of failed reads before giving the job back as recoverable.
const LEGACY_MAX_READ_FAILURES = 20;

export function createOnceOnlySubscribe(fal, { subscribeOnce = subscribeFalQueueOnceWithCancellation } = {}) {
  return async function subscribe(endpointId, options = {}) {
    // The queue owner cancels the provider job for both an aborted signal and a cancellation
    // thrown from onQueueUpdate (the legacy checkCancelled style), and hands back the caller's error.
    const { abortSignal, timeout, pollInterval, ...rest } = options;
    try {
      return await subscribeOnce(fal, endpointId, rest, abortSignal, {
        timeoutMs: timeout || LEGACY_SUBSCRIBE_TIMEOUT_MS,
        pollIntervalMs: pollInterval || LEGACY_POLL_INTERVAL_MS,
        maxReadFailures: LEGACY_MAX_READ_FAILURES,
      });
    } catch (error) {
      const status = error?.status || 0;
      if (!abortSignal?.aborted && !isCancellationError(error) && !(status >= 400 && status < 500)) {
        captureSubsystemError(error, 'fal-api', { endpointId });
      }
      throw error;
    }
  };
}
